/**
 * Taking a photograph, and getting it to the server eventually.
 *
 * Two things make this harder than an upload box.
 *
 * A phone camera produces a four-megabyte picture, and this app runs on a 2G edge of
 * a network where that will never finish. So every photo is drawn onto a canvas at a
 * sane size and re-encoded as a JPEG before it goes anywhere — around a hundred
 * kilobytes, which is plenty to read a scale display or show a crate of bruised
 * tomatoes.
 *
 * And an agent at a farm gate usually has no signal at all. The photo is held on the
 * device with the work it belongs to and uploaded when there is a network, the same
 * way the weighing itself is. Until then the record carries a local reference, which
 * is swapped for the real one the moment the upload lands.
 */
import { tx } from './idb'
import { api, session } from './api'
import { farmerSession } from './farmer'
import { buyerSession } from './buyer'

/** Long enough to read a scale display; small enough to send on a bad line. */
const MAX_EDGE = 1280
const QUALITY = 0.72

export type PhotoKind =
  | 'farmgate_scale' | 'depot_scale' | 'pod' | 'rejection' | 'return'
  | 'farmer_id' | 'farmer_portrait' | 'offer' | 'grade_spec' | 'expense'
  // Not a photograph, but everything about getting it to the server is the same:
  // hashed, stored once, queued when there is no signal.
  | 'signature'
  | 'other'

export interface PendingPhoto {
  localId: string
  kind: PhotoKind
  blob: Blob
  lat: number | null
  lng: number | null
  accuracyM: number | null
  takenAt: string
}

/** A photo reference: `media:<id>` once uploaded, `local:<id>` while it is waiting. */
export type PhotoRef = string

export const isLocal = (ref: PhotoRef | null | undefined) => !!ref?.startsWith('local:')
export const mediaId = (ref: PhotoRef | null | undefined) =>
  ref?.startsWith('media:') ? ref.slice(6) : null

/** The URL the console and the apps display a stored photo from. */
export const photoUrl = (ref: PhotoRef | null | undefined) => {
  const id = mediaId(ref)
  return id ? `/api/media/${id}` : null
}

// ---------------------------------------------------------------- shrinking
async function shrink(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
  const w = Math.round(bitmap.width * scale)
  const h = Math.round(bitmap.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This phone cannot process the photo.')
  ctx.drawImage(bitmap, 0, 0, w, h)
  bitmap.close()

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', QUALITY))
  if (!blob) throw new Error('Could not save the photo.')
  return blob
}

// ---------------------------------------------------------------- the queue
const pending = {
  async put(p: PendingPhoto) {
    await tx('photos', 'readwrite', (s) => s.put(p))
  },
  async get(localId: string) {
    return tx<PendingPhoto | undefined>('photos', 'readonly', (s) => s.get(localId))
  },
  async all() {
    return tx<PendingPhoto[]>('photos', 'readonly', (s) => s.getAll())
  },
  async drop(localId: string) {
    await tx('photos', 'readwrite', (s) => s.delete(localId))
  },
}

async function upload(p: PendingPhoto): Promise<string> {
  const form = new FormData()
  // The server reads the content type, not the name, but a signature saved as .jpg is
  // confusing to anybody who later goes looking at the files on disk.
  const ext = p.blob.type === 'image/png' ? 'png' : p.blob.type === 'image/webp' ? 'webp' : 'jpg'
  form.append('file', p.blob, `${p.kind}.${ext}`)
  form.append('kind', p.kind)
  if (p.lat !== null) form.append('lat', String(p.lat))
  if (p.lng !== null) form.append('lng', String(p.lng))
  if (p.accuracyM !== null) form.append('accuracyM', String(p.accuracyM))
  form.append('takenAt', p.takenAt)

  // Staff hold their token in memory inside api.ts, so that client owns the request
  // and its refresh-on-401. A farmer or a buyer carries a long-lived token this module
  // can read straight from their own session.
  const portalToken = farmerSession.token ?? buyerSession.token
  const res = session.isSignedIn
    ? await api.upload('/media', form)
    : await fetch('/api/media', {
        method: 'POST',
        body: form,
        headers: portalToken ? { Authorization: `Bearer ${portalToken}` } : {},
      })

  if (!res.ok) throw new Error(`Upload failed (${res.status})`)
  const saved = (await res.json()) as { id: string }
  return `media:${saved.id}`
}

export const photos = {
  /**
   * Shrinks the picture and keeps it on the device. Returns the reference to store
   * against the work; it is uploaded now if there is a network, and later if not.
   */
  async capture(
    file: File,
    kind: PhotoKind,
    where: { lat: number; lng: number; accuracy: number } | null,
  ): Promise<{ ref: PhotoRef; preview: string; uploaded: boolean }> {
    return photos.keep(await shrink(file), kind, where)
  },

  /**
   * The same, for bytes this app drew rather than a camera produced — a signature.
   * It skips the downscale: the canvas is already small, and re-encoding a black mark
   * on white as a JPEG makes it grey and fuzzy for no saving worth having.
   */
  async keep(
    blob: Blob,
    kind: PhotoKind,
    where: { lat: number; lng: number; accuracy: number } | null,
  ): Promise<{ ref: PhotoRef; preview: string; uploaded: boolean }> {
    const localId = crypto.randomUUID()
    const p: PendingPhoto = {
      localId,
      kind,
      blob,
      lat: where?.lat ?? null,
      lng: where?.lng ?? null,
      accuracyM: where?.accuracy ?? null,
      takenAt: new Date().toISOString(),
    }

    await pending.put(p)
    const preview = URL.createObjectURL(blob)

    if (navigator.onLine) {
      try {
        const ref = await upload(p)
        await pending.drop(localId)
        return { ref, preview, uploaded: true }
      } catch {
        // Keep it. The flush below will try again.
      }
    }

    return { ref: `local:${localId}`, preview, uploaded: false }
  },

  /**
   * Sends everything still waiting and reports what each local reference became, so
   * the outbox can rewrite its payloads before it sends them.
   */
  async flush(): Promise<Record<string, string>> {
    const map: Record<string, string> = {}
    if (!navigator.onLine) return map

    for (const p of await pending.all()) {
      try {
        map[`local:${p.localId}`] = await upload(p)
        await pending.drop(p.localId)
      } catch {
        // Leave it for the next attempt rather than losing the evidence.
      }
    }
    return map
  },

  async waiting(): Promise<number> {
    return (await pending.all()).length
  },

  /** A preview for a photo that has not been uploaded yet. */
  async localPreview(ref: PhotoRef | null | undefined): Promise<string | null> {
    if (!ref || !isLocal(ref)) return null
    const p = await pending.get(ref.slice(6))
    return p ? URL.createObjectURL(p.blob) : null
  },

  /**
   * Fetches a stored photo for display. Reading one needs a token, so this cannot be
   * a bare <img src> — the bytes come back through the authenticated client and are
   * handed to the browser as an object URL.
   */
  async fetchBlobUrl(ref: PhotoRef | null | undefined): Promise<string | null> {
    const id = mediaId(ref)
    if (!id) return null

    const portalToken = farmerSession.token ?? buyerSession.token
    const res = session.isSignedIn
      ? await api.fetch(`/media/${id}`)
      : await fetch(`/api/media/${id}`, {
          headers: portalToken ? { Authorization: `Bearer ${portalToken}` } : {},
        })

    if (!res.ok) return null
    return URL.createObjectURL(await res.blob())
  },
}
